/* eslint-disable no-param-reassign, max-lines, max-params */
import { api } from "jiffy-api";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import {
  trackImageUploadStart,
  trackImageUploadCompleted,
  trackImageProcessingCompleted,
  trackUploadError
} from "../utils/tracker";
import initCable from "../dtf-channel";

const DEFAULT_FRONT_SURFACE = {
  picture: remoteImageStaticUrl(
    "1dc4f3c3672e233b1bac73a07e61c73d_5589000e149a78eb77b8554c98987d9e3a8a.jpg"
  ),
  ppi: 112.83,
  boundingBox: {
    top: 1932,
    left: 1323,
    width: 1252,
    height: 1579
  },
  surfaceLocationOffset: {
    topX: 1879.0,
    topY: 1593.0,
    bottomX: 1879.0,
    bottomY: 1932.0
  },
  isDark: false
};

const initialState = {
  enhancePreselected: false,
  temporalComment: "",
  temporalEnhanced: false,
  downscaleFactor: 1,
  dtfChannelUuid: "",
  artwork: null,
  uploadedImage: null,
  artworkSize: {
    width: null,
    height: null
  },
  artworkSizeInches: {
    width: null,
    height: null
  },
  isNewArtwork: false,
  currentSurface: "front",
  editType: null
};

const artworkModule = {
  namespaced: true,

  state() {
    return { ...initialState };
  },

  getters: {
    removeBackground: (_, getters) => getters.artwork?.remove_background,
    artworkUrl: (state, { artwork, removeBackground }) => {
      if (!artwork) return URL.createObjectURL(state.uploadedImage || new Blob());
      const {
        enhanced,
        preloaded,
        original_image_url: origBg,
        no_bg_url: noBg,
        with_bg_url: wBg,
        url: originalUrl
      } = artwork;
      if (preloaded) return originalUrl;
      if (!enhanced) return origBg;
      if (removeBackground) return noBg || wBg;
      return wBg;
    },
    comment: (state, getters) =>
      getters.artwork ? getters.artwork.comment : state.temporalComment,
    enhanced: (state, getters) =>
      getters.artwork ? getters.artwork.enhanced : state.temporalEnhanced,
    enhancePreselected: state => state.enhancePreselected,
    artworkSize: state => state.artworkSize,
    convertPxToInches: (_, getters) => px =>
      (px / getters.surface.ppi / getters.downscaleFactor).toFixed(2),
    convertInchesToPx: (_, getters) => inches =>
      inches * getters.surface.ppi * getters.downscaleFactor,
    downscaleFactor: state => state.downscaleFactor,
    artwork: state => state.artwork,
    surfaceLabel: state => state.currentSurface,
    surface: (_, getters) => getters.getSurface(getters.surfaceLabel),
    getSurface: (_, getters, __, rootGetters) => surface => {
      const surfaces = rootGetters["product/selectedCompositionSurfaces"];
      if (surfaces) {
        return surfaces[surface] || surfaces[Object.keys(surfaces)[0]];
      }

      return DEFAULT_FRONT_SURFACE;
    },
    artworkExists: (_, getters, __, rootGetters) =>
      getters.artwork || rootGetters["loading/uploadArtwork"],
    editType: state => state.editType
  },

  mutations: {
    setRemoveBackground(state, isRemoved) {
      state.artwork.remove_background = isRemoved;
    },
    setDownscaleFactor(state, factor) {
      state.downscaleFactor = factor;
    },
    setSurface(state, surface) {
      state.currentSurface = surface;
    },
    setArtwork(state, artwork) {
      state.artwork = artwork;
    },
    setArtworkSize(state, size) {
      state.artworkSize = size;
    },
    clearArtworkSize(state) {
      state.artworkSize = { width: null, height: null };
    },
    setArtworkSizeInches(state, size) {
      state.artworkSizeInches = size;
    },
    setTemporalEnhanced(state, enhanced) {
      state.temporalEnhanced = enhanced;
    },
    setTemporalComment(state, comment) {
      state.temporalComment = comment;
    },
    setArtworkEnhanced(state, enhanced) {
      state.artwork.enhanced = enhanced;
    },
    setEnhancePreselected(state, enhancePreselected) {
      state.enhancePreselected = enhancePreselected;
    },
    setArtworkComment(state, comment) {
      state.artwork.comment = comment;
    },
    setEditType(state, value) {
      state.editType = value;
    }
  },

  actions: {
    async initArtworkSize({ commit, dispatch, getters, state }, size) {
      const { width: widthInch, height: heightInch } = state.artworkSizeInches;

      // Load the artwork size from saved current order
      if (widthInch && heightInch) {
        commit("setArtworkSize", {
          width: getters.convertInchesToPx(widthInch),
          height: getters.convertInchesToPx(heightInch)
        });
        commit("setArtworkSizeInches", { width: null, height: null });
        // Load the new artwork from just uploaded image
      } else if (state.isNewArtwork) {
        commit("setArtworkSize", size);
        await dispatch("checkout/createPlacement", null, { root: true });
        state.isNewArtwork = false;
        commit("checkout/setCurrentPlacementAction", null, { root: true });
        // Update placement just after changing the artwork position: front, back,
        // and remove background
      } else {
        await dispatch("updateArtworkSize", size);
      }
    },
    async updateArtworkSize({ commit, dispatch }, size) {
      commit("setArtworkSize", size);
      await dispatch("checkout/updatePlacement", null, { root: true });
    },
    async upload({ state, commit, dispatch }, file) {
      let uploadStartTime;

      try {
        commit("loading/setUploadArtwork", true, { root: true });
        commit("loading/setUploadArtworkStatus", "upload", { root: true });
        dispatch("loading/incrementUploadArtworkProgress", null, { root: true });
        state.uploadedImage = file;

        const controller = new AbortController();
        const { signal } = controller;
        const formData = new FormData();
        const processTimeStamp = Date.now();

        formData.append("file", file);
        formData.append("product_id", 432292);
        formData.append("file_original_index", 0);
        formData.append("dtf_channel_uuid", state.dtfChannelUuid);
        formData.append("pre_cut", false);
        formData.append("process_time_stamp", processTimeStamp);
        formData.append("print", true);

        trackImageUploadStart(file);

        uploadStartTime = performance.now();

        const artwork = await api.dtf.uploadArtwork(formData, {
          raw: true,
          useAxiosForRequest: true,
          headers: { "Content-Type": undefined },
          signal
        });
        commit("setArtwork", { ...artwork, preloaded: true, enhanced: false });
        state.isNewArtwork = true;

        trackImageUploadCompleted({
          file,
          uploadStartTime,
          artwork
        });
      } catch (error) {
        dispatch("handleUploadError", { error, file, uploadStartTime });
        commit("setCurrentPage", "welcome", { root: true });
      }
    },
    /* eslint-disable max-lines-per-function */
    async createDtfChannel({ state, commit, dispatch }) {
      const response = await api.dtf.getInitConfig({ catalog_number: "DTF001" });
      const { success, error, dtfChannelUuid } = objNamesToCamelCase(response);

      if (!success) throw new Error(error); // attempt to retrieve error from server if present

      state.dtfChannelUuid = dtfChannelUuid;

      initCable(dtfChannelUuid, {
        received(data) {
          const { result, action } = data;
          const { artwork } = result;
          if (
            result.error &&
            ["upload_artwork", "process_artwork", "validate_artwork"].includes(action)
          ) {
            dispatch("handleProcessingError", { artwork, result });
          }

          if (action === "upload_artwork") {
            this.perform("process_dtf_artwork", data.data);
            commit("loading/setUploadArtworkStatus", "validate", { root: true });
          }

          if (action === "validate_artwork") {
            commit("loading/setUploadArtworkStatus", "process", { root: true });
          }

          if (action === "process_artwork") {
            commit("setArtwork", {
              ...result.process_data,
              enhanced: state.temporalEnhanced,
              comment: state.temporalComment
            });
            commit("loading/setUploadArtworkStatus", "complete", { root: true });
            trackImageProcessingCompleted({
              result,
              dtfChannelUuid,
              action
            });
          }
        }
      });
    },
    handleUploadError({ getters, commit }, { error, file, uploadStartTime }) {
      commit("loading/setUploadArtwork", false, { root: true });
      commit(
        "errors/addNewError",
        {
          title: "upload",
          error: error.message
        },
        { root: true }
      );

      if (error.name === "AbortError" || error.name === "CanceledError") return;

      const errorResponse = error?.response?.data || error;

      const handledError = ["backend_file_validation", "backend_processing_error"].includes(
        errorResponse?.error_type
      );

      if (
        (errorResponse.error && !errorResponse.success) ||
        (navigator.onLine && error.message !== "Network Error")
      ) {
        if (handledError) {
          trackImageUploadCompleted({
            file,
            response: errorResponse,
            uploadStartTime,
            artwork: getters.artwork
          });
        }
        trackUploadError({ file, errorResponse });
      }
    },
    handleProcessingError({ state, commit }, { artwork, result }) {
      commit(
        "errors/addNewError",
        {
          title: "createDtfChannel",
          error: result.error,
          details: [{ title: "type", description: result.error_type }]
        },
        { root: true }
      );
      commit("loading/setUploadArtwork", false, { root: true });

      state.uploadedImage = null;

      if (result.error && !result.success) {
        trackUploadError({
          file: {
            name: artwork?.name,
            type: artwork?.filetype,
            size: artwork?.filesize,
            width: artwork?.metadata?.width,
            height: artwork?.metadata?.height
          },
          errorResponse: result
        });
      }
    }
  }
};

export default artworkModule;
